import { useState } from 'react';
import { Link } from 'react-router-dom';
import Accordion from '../components/Accordion';
import Layout from '../components/Layout';
import ProofStrip, { ProofItem } from '../components/ProofStrip';
import SectionHeader from '../components/SectionHeader';
import { Icon } from '../components/icons';
import { SITE } from '../data/site';
import { usePageMeta } from '../hooks/usePageMeta';
import './Microsoft365Setup.css';

const READINESS_ITEMS = [
  "I know whether we're a for-profit business or a registered nonprofit",
  'We have a domain name (e.g. yourbusiness.com) — or we want help picking one',
  'I know roughly how many people will need an email account',
  'I can get someone to make a couple of DNS changes with us (or give temporary access) for about 15 minutes',
  '(Nonprofits) I have our registration/charity number handy',
];

const PROOF_ITEMS: ProofItem[] = [
  {
    icon: 'award',
    label: 'Microsoft Certified',
    value: SITE.certifications.map(c => c.code).join(', '),
  },
  {
    icon: 'check',
    label: 'Starting at $500 CAD',
    value: 'One-time fee, no contracts, no markup on licensing',
  },
  {
    icon: 'speed',
    label: 'Live in days',
    value: 'Most setups complete within a few business days',
  },
  {
    icon: 'heart',
    label: 'Nonprofit pricing',
    value: 'Eligible orgs often qualify for no-cost licensing',
  },
];

const APPS = [
  { letter: 'O', name: 'Outlook', color: '#0078D4' },
  { letter: 'T', name: 'Teams', color: '#6264A7' },
  { letter: 'S', name: 'SharePoint & OneDrive', color: '#038387' },
  { letter: 'W', name: 'Word', color: '#2B579A' },
  { letter: 'X', name: 'Excel', color: '#217346' },
  { letter: 'P', name: 'PowerPoint', color: '#B7472A' },
];

const FAQ_ITEMS = [
  {
    id: 'cost',
    question: 'How much does this cost?',
    answer:
      'Setup starts at a $500 CAD minimum for teams of up to 5 users, plus $25 CAD per additional user. Your exact quote depends on your team size and organization type — we confirm the total on the intake call before any work starts.',
  },
  {
    id: 'domain',
    question: 'Do I need to already have a domain?',
    answer:
      "No — if you don't have one yet, we'll help you pick and set one up as part of the intake call.",
  },
  {
    id: 'timeline',
    question: 'How long does setup take?',
    answer:
      'Most setups are completed within a few business days, accounting for standard domain verification wait times.',
  },
  {
    id: 'migration',
    question: "We're on Google Workspace (or another provider). Can you move us over?",
    answer:
      'Yes. We migrate existing mailboxes, contacts, calendars, and files into your new Microsoft 365 tenant, and cut email over so nothing is lost in the switch.',
  },
  {
    id: 'subscription',
    question: 'Is this a subscription?',
    answer:
      "No. This is a one-time service fee, not a subscription. Any ongoing Microsoft licensing costs are billed directly by Microsoft — we don't mark them up.",
  },
  {
    id: 'licensing',
    question: 'Can we get free or discounted licensing?',
    answer:
      "Many small organizations and nonprofits qualify for no-cost licensing options, which we'll identify and apply for you during setup.",
  },
];

function Microsoft365Setup() {
  usePageMeta({
    title: 'Microsoft 365 Setup for Small Businesses & Nonprofits | EightyThreeLabs',
    description:
      'Professional Microsoft 365 setup for small businesses and nonprofits — email, Teams, and SharePoint, done for you, starting at $500 CAD. Migrations from Google Workspace included.',
  });

  return (
    <Layout>
      {/* HERO */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <span className="section-label">MICROSOFT 365 SETUP</span>
            <h1 className="hero-title">
              Microsoft 365 Setup for
              <br />
              <span className="hero-title-accent">Small Businesses &amp; Nonprofits</span>
            </h1>
            <p className="hero-description">
              Get your professional email, Microsoft Teams, and SharePoint up and running — done for
              you, one time, no ongoing contracts. We handle the entire setup, start to finish, so
              you can focus on running your business instead of wrestling with tenant configuration,
              domain records, and licensing.
            </p>
            <div className="cta-buttons">
              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Book a Free Intake Call
              </a>
              <a href="#included" className="btn-secondary">
                See What&rsquo;s Included
              </a>
            </div>
            <div className="m365-trust-strip">
              <span>
                <CheckIcon />
                Starting at $500 CAD
              </span>
              <span>
                <CheckIcon />
                One-time fee, no contracts
              </span>
              <span>
                <CheckIcon />
                Nonprofit pricing available
              </span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="m365-email-card">
              <div className="m365-email-card-label">Your inbox, before &amp; after</div>
              <div className="m365-email-row m365-email-row-before">
                <MailIcon />
                you@gmail.com
              </div>
              <div className="m365-email-arrow">
                <ArrowDownIcon />
              </div>
              <div className="m365-email-row m365-email-row-after">
                <MailIcon />
                you@yourbusiness.com
              </div>
              <div className="m365-app-chip-row">
                {APPS.map(app => (
                  <span
                    key={app.name}
                    className="m365-app-chip-mini"
                    style={{ background: app.color }}
                    title={app.name}
                  >
                    {app.letter}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="hero-wave"></div>
      </section>

      <ProofStrip items={PROOF_ITEMS} />

      {/* 01 WHO THIS IS FOR */}
      <section id="who" className="section section-white">
        <div className="container">
          <SectionHeader
            number="01"
            eyebrow="WHO THIS IS FOR"
            title="Built for teams who'd rather run their business than run IT"
            align="left"
          />

          <div className="services-grid reveal">
            <div className="service-card">
              <div className="service-icon service-icon-blue">
                <Icon id="briefcase" size={28} />
              </div>
              <h3 className="service-title">Small Businesses</h3>
              <p className="service-description">
                Just getting started, or moving off a personal Gmail/Outlook account onto a real
                business domain.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon service-icon-green">
                <Icon id="heart" size={28} />
              </div>
              <h3 className="service-title">Nonprofits &amp; Community Orgs</h3>
              <p className="service-description">
                Want a proper Microsoft environment for their team, with eligibility for special
                nonprofit licensing.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon service-icon-purple">
                <UserSparkIcon />
              </div>
              <h3 className="service-title">Founders &amp; Small Teams</h3>
              <p className="service-description">
                Need this working correctly the first time — without spending hours learning
                Microsoft&rsquo;s admin tools.
              </p>
            </div>
          </div>

          <p className="m365-section-note reveal">
            If you&rsquo;ve been putting off setting up &ldquo;real&rdquo; business email because it
            feels technical or confusing, this is exactly the problem we solve.
          </p>
        </div>
      </section>

      {/* 02 WHAT'S INCLUDED */}
      <section id="included" className="section section-gray">
        <div className="container">
          <SectionHeader
            number="02"
            eyebrow="ONE FLAT FEE, FULLY DONE FOR YOU"
            title="What's included"
            align="left"
          />

          <div className="features-grid m365-included-grid reveal">
            <div className="feature-item">
              <div className="feature-icon">
                <TenantIcon />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">Microsoft 365 Tenant Setup</h4>
                <p className="feature-desc">
                  Your organization&rsquo;s private, secure environment in Microsoft&rsquo;s cloud.
                </p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <GlobeIcon />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">Custom Domain Connected</h4>
                <p className="feature-desc">
                  Your own domain (yourbusiness.com) verified and linked, so your email looks
                  professional instead of a generic address.
                </p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <Icon id="advisory" size={24} />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">User Accounts Created</h4>
                <p className="feature-desc">Set up for you and your team under your own domain.</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <LicenseIcon />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">Licensing Configured</h4>
                <p className="feature-desc">
                  We identify and apply the right no-cost or lowest-cost licensing option for your
                  organization type, including special options for eligible nonprofits.
                </p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <MailIcon />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">Email Fully Switched Over</h4>
                <p className="feature-desc">
                  All the technical DNS work — the part that trips most people up — handled and
                  verified, so mail reliably lands in your new inbox.
                </p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <Icon id="document" size={24} />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">Simple Handover</h4>
                <p className="feature-desc">
                  A plain-English document showing your login details, what was set up, and how to
                  start using your email, Teams, and SharePoint immediately.
                </p>
              </div>
            </div>
          </div>

          <div className="svc-challenge m365-migration reveal reveal-delay-1">
            <div className="m365-migration-icon">
              <Icon id="reporting" size={22} />
            </div>
            <div>
              <h3>Already on Google Workspace or another provider?</h3>
              <p>
                We migrate your existing mailboxes, contacts, calendars, and files into the new
                tenant and cut email over cleanly — nothing lost, no gap in service. Migration is
                scoped on the intake call and quoted alongside the setup.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 03 WHAT YOU GET */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="03"
            eyebrow="WHAT YOU GET AT THE END"
            title="A working, professional Microsoft 365 environment"
            align="left"
          />

          <div className="m365-apps-row reveal">
            {APPS.map(app => (
              <div className="m365-app-chip" key={app.name}>
                <span className="m365-app-chip-icon" style={{ background: app.color }}>
                  {app.letter}
                </span>
                <span>{app.name}</span>
              </div>
            ))}
          </div>

          <p className="m365-apps-note reveal">
            All set up correctly, the first time — no guesswork, no half-finished DNS records, no
            confusing admin portals for you to figure out.
          </p>
        </div>
      </section>

      {/* 04 HOW IT WORKS */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            number="04"
            eyebrow="HOW IT WORKS"
            title="Three steps, start to finish"
            align="left"
          />

          <div className="approach-grid m365-process-grid reveal">
            <div className="approach-step">
              <div className="approach-number">01</div>
              <h3 className="approach-title">Quick Intake Call</h3>
              <p className="approach-desc">
                We ask a few questions about your business or organization, your domain (or help you
                pick one if you don&rsquo;t have one yet), and your team size.
              </p>
            </div>
            <div className="approach-step">
              <div className="approach-number">02</div>
              <h3 className="approach-title">We Do the Setup</h3>
              <p className="approach-desc">
                Tenant creation, domain verification, user accounts, licensing, migration if needed,
                and email cutover — handled entirely by us.
              </p>
            </div>
            <div className="approach-step">
              <div className="approach-number">03</div>
              <h3 className="approach-title">Handover</h3>
              <p className="approach-desc">
                You receive your login details and a simple guide, plus a short walkthrough so
                you&rsquo;re confident using everything from day one.
              </p>
            </div>
          </div>

          <p className="m365-section-note reveal">
            Most setups are completed within a few business days, accounting for standard domain
            verification wait times.
          </p>
        </div>
      </section>

      {/* 05 PRICING */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="05"
            eyebrow="PRICING"
            title="One flat fee. Licensing billed by Microsoft, never marked up."
            align="left"
          />

          <div className="m365-pricing-band reveal">
            <div>
              <span className="m365-pricing-tag">Starting at</span>
              <h2>$500 CAD</h2>
              <p>
                Every engagement starts with a $500 CAD minimum, covering tenant setup, domain
                verification, licensing, and email cutover for teams of up to 5 users. Larger teams
                add $25 CAD per additional user for account creation, license assignment, and
                training. This is a one-time service fee — not a subscription.
              </p>
            </div>
            <div className="m365-pricing-points">
              <div className="m365-pricing-point">
                <CheckIcon />
                $500 CAD minimum covers setup for teams up to 5 users
              </div>
              <div className="m365-pricing-point">
                <CheckIcon />
                +$25 CAD per additional user beyond 5
              </div>
              <div className="m365-pricing-point">
                <CheckIcon />
                Microsoft licensing billed directly by Microsoft, never marked up
              </div>
              <div className="m365-pricing-point">
                <CheckIcon />
                Many small organizations and nonprofits qualify for no-cost licensing
              </div>
            </div>
          </div>

          <div className="m365-licensing-note reveal reveal-delay-1">
            <div className="m365-licensing-note-icon">
              <InfoIcon />
            </div>
            <div>
              <h3>Microsoft licensing is separate from our fee</h3>
              <p>
                Our $500 CAD+ fee covers the setup work only. The Microsoft 365 plan itself (Teams,
                Word, Excel, Outlook, and the rest) is billed directly by Microsoft in CAD, based on
                whichever plan fits your organization — we don&rsquo;t mark it up or add it to your
                invoice.
              </p>
              <div className="m365-licensing-links">
                <a
                  href="https://www.microsoft.com/microsoft-365/business/microsoft-365-plans-and-pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See Microsoft 365 Business plans &amp; pricing (CAD) ↗
                </a>
                <a
                  href="https://www.microsoft.com/en-ca/nonprofits/microsoft-365"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Microsoft 365 nonprofit licensing options ↗
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 06 FAQ */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            number="06"
            eyebrow="COMMON QUESTIONS"
            title="What people usually ask"
            align="left"
          />
          <div className="faq-list reveal">
            <Accordion items={FAQ_ITEMS.map(f => ({ ...f, answer: <p>{f.answer}</p> }))} />
          </div>
        </div>
      </section>

      {/* READINESS CHECKLIST */}
      <section className="section section-white">
        <div className="container">
          <div className="reveal">
            <ReadinessChecklist />
          </div>
        </div>
      </section>

      {/* NEXT STEP → AUTOMATION */}
      <section className="section section-gray">
        <div className="container">
          <div className="m365-callout reveal">
            <div className="m365-callout-icon">
              <Icon id="automation" size={40} />
            </div>
            <div className="m365-callout-text">
              <span className="section-label">ALREADY SET UP?</span>
              <h3 className="m365-callout-title">
                The same tenant is the foundation for automation
              </h3>
              <p className="m365-callout-desc">
                Once your data lives in SharePoint, Dataverse, and Microsoft 365, the repetitive
                work around it — invoices, approvals, onboarding, Monday reports — can run itself.
                That&rsquo;s the part of the business we love most.
              </p>
            </div>
            <div className="m365-callout-actions">
              <Link to="/services/automation-workflows" className="btn-primary">
                See Automation Workflows
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="contact" className="section section-white">
        <div className="container">
          <div className="m365-final-cta reveal">
            <h2>Ready to get your Microsoft environment set up the right way?</h2>
            <p>
              We specialize in Microsoft 365, Power Platform, and business automation — this setup
              service is the essential first step we offer to help small businesses and nonprofits
              build on a solid, professional technology foundation from day one.
            </p>
            <div className="cta-buttons m365-final-cta-actions">
              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Book a Free Intake Call
              </a>
              <a href={`mailto:${SITE.contact.email}`} className="btn-secondary">
                Email Us Instead
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function ReadinessChecklist() {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState<boolean[]>(() => READINESS_ITEMS.map(() => false));

  const toggleItem = (index: number) => {
    setChecked(prev => prev.map((value, i) => (i === index ? !value : value)));
  };

  return (
    <div className="m365-checklist-card">
      <div className="m365-checklist-intro">
        <h3>Not sure you&rsquo;re ready to book a call?</h3>
        <p>Check these off first — it&rsquo;ll make your intake call faster.</p>
      </div>

      <button
        type="button"
        className="btn-secondary m365-checklist-toggle"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        {open ? 'Hide the checklist' : 'Show the checklist'}
      </button>

      {open && (
        <div>
          <ul className="m365-checklist-items">
            {READINESS_ITEMS.map((item, index) => (
              <li key={item}>
                <button
                  type="button"
                  className={`m365-checklist-item${checked[index] ? ' m365-checklist-item-checked' : ''}`}
                  onClick={() => toggleItem(index)}
                  aria-pressed={checked[index]}
                >
                  <span className="m365-checklist-box">{checked[index] && <CheckIcon />}</span>
                  <span>{item}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="m365-checklist-footnote">
            Don&rsquo;t have all of these yet? No problem — we&rsquo;ll help you sort out anything
            missing on the intake call.
          </p>
        </div>
      )}
    </div>
  );
}

/* ---- page-local icons not in the shared registry ---- */

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

function UserSparkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="10" cy="8" r="4" />
      <path d="M2 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 1.5.19" />
      <path d="M19 16l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  );
}

function TenantIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.5 19a4.5 4.5 0 1 0 0-9 6 6 0 0 0-11.6-1.5A4.5 4.5 0 0 0 6.5 19h11z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function LicenseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24H4a1 1 0 0 0-1 1v5.59a2 2 0 0 0 .59 1.41l9.58 9.59a2 2 0 0 0 2.82 0l4.6-4.6a2 2 0 0 0 0-2.82z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}

export default Microsoft365Setup;
